import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { Search, Clock, Package, Check, Zap } from 'lucide-react';

const RECENT_PRODUCTS_KEY = 'nirala_recent_products';
const MAX_RECENT = 10;

/**
 * QuickAddProduct - Fast search-and-add component for invoice line items
 * Supports keyboard-only operation for POS-style workflow
 */
const QuickAddProduct = ({ 
  products, 
  inventory, 
  onAddProduct,
  disabled = false 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentProducts, setRecentProducts] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Load recent products from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(RECENT_PRODUCTS_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentProducts(parsed);
      } catch (e) {
        console.error('Failed to parse recent products');
      }
    }
  }, []);

  // Save recent product to localStorage
  const saveRecentProduct = useCallback((product) => {
    setRecentProducts(prev => {
      // Remove if already exists
      const filtered = prev.filter(p => p.id !== product.id);
      // Add to beginning
      const updated = [
        { 
          id: product.id, 
          name: product.name, 
          size: product.size, 
          brand: product.brand,
          selling_price: product.selling_price,
          gst_percentage: product.gst_percentage
        }, 
        ...filtered
      ].slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_PRODUCTS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Get stock for a product
  const getProductStock = useCallback((productId) => {
    const productInventory = inventory.filter(inv => inv.product_id === productId);
    return productInventory.reduce((sum, inv) => sum + (inv.quantity || 0), 0);
  }, [inventory]);

  // Get latest batch for a product
  const getLatestBatch = useCallback((productId) => {
    const productInventory = inventory
      .filter(inv => inv.product_id === productId && inv.quantity > 0)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return productInventory[0]?.batch_number || '';
  }, [inventory]);

  // Normalize search term for flexible matching
  const normalizeSearch = (str) => {
    return str.toLowerCase()
      .replace(/[×x]/g, '')  // Remove x or × 
      .replace(/\s+/g, '')   // Remove spaces
      .replace(/\./g, '');   // Remove dots
  };

  // Filter products based on search
  const filteredProducts = searchTerm.length > 0 
    ? products.filter(product => {
        const search = normalizeSearch(searchTerm);
        const name = normalizeSearch(product.name || '');
        const sku = normalizeSearch(product.sku || '');
        const size = normalizeSearch(product.size || '');
        const brand = normalizeSearch(product.brand || '');
        
        return name.includes(search) || 
               sku.includes(search) || 
               size.includes(search) ||
               brand.includes(search) ||
               // Also match partial numbers (e.g., "375" matches "3.75")
               size.replace(/\./g, '').includes(search);
      }).slice(0, 10)
    : [];

  // Get display items (filtered or recent)
  const displayItems = searchTerm.length > 0 
    ? filteredProducts 
    : recentProducts.map(rp => products.find(p => p.id === rp.id)).filter(Boolean).slice(0, 5);

  const showRecent = searchTerm.length === 0 && recentProducts.length > 0;

  // Handle product selection
  const handleSelectProduct = useCallback((product) => {
    const stock = getProductStock(product.id);
    const latestBatch = getLatestBatch(product.id);
    
    if (stock <= 0) {
      return; // Don't select out of stock items
    }

    // Save to recent
    saveRecentProduct(product);

    // Add product to invoice
    onAddProduct({
      product_id: product.id,
      product_name: product.name,
      size: product.size,
      brand: product.brand,
      batch_number: latestBatch,
      quantity: 1,
      selling_price: product.selling_price || '',
      gst_percentage: product.gst_percentage || 12
    });

    // Reset search
    setSearchTerm('');
    setIsOpen(false);
    setSelectedIndex(0);
  }, [getProductStock, getLatestBatch, saveRecentProduct, onAddProduct]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen && e.key === 'ArrowDown') {
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    const items = displayItems;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (items[selectedIndex]) {
          handleSelectProduct(items[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  }, [isOpen, displayItems, selectedIndex, handleSelectProduct]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && isOpen) {
      const selectedItem = listRef.current.children[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, isOpen]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <Zap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Quick Add: Type product name, size (e.g., 375, 3.75x10)..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="pl-10 pr-4 h-12 text-base bg-teal-50/50 border-teal-200 focus:border-teal-400 focus:ring-teal-400 placeholder:text-slate-400"
          data-testid="quick-add-search"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-slate-100 px-1.5 font-mono text-[10px] text-slate-500">
            ↑↓
          </kbd>
          <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-slate-100 px-1.5 font-mono text-[10px] text-slate-500">
            Enter
          </kbd>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (displayItems.length > 0 || showRecent) && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden"
          data-testid="quick-add-dropdown"
        >
          {/* Header */}
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              {showRecent ? (
                <>
                  <Clock className="w-3 h-3" />
                  <span>Recent Products</span>
                </>
              ) : (
                <>
                  <Search className="w-3 h-3" />
                  <span>{filteredProducts.length} products found</span>
                </>
              )}
            </div>
          </div>

          {/* Product List */}
          <div ref={listRef} className="max-h-64 overflow-y-auto">
            {displayItems.map((product, index) => {
              const stock = getProductStock(product.id);
              const hasStock = stock > 0;
              const isSelected = index === selectedIndex;

              return (
                <button
                  key={product.id}
                  onClick={() => handleSelectProduct(product)}
                  disabled={!hasStock}
                  className={cn(
                    "w-full px-3 py-2.5 flex items-center justify-between text-left transition-colors",
                    isSelected && hasStock && "bg-teal-50",
                    hasStock ? "hover:bg-slate-50 cursor-pointer" : "opacity-50 cursor-not-allowed",
                    index !== displayItems.length - 1 && "border-b border-slate-100"
                  )}
                  data-testid={`quick-add-item-${product.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "font-medium text-sm truncate",
                        isSelected && hasStock ? "text-teal-700" : "text-slate-900"
                      )}>
                        {product.name}
                      </span>
                      {product.size && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-slate-100">
                          {product.size}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {product.brand && (
                        <span className="text-xs text-slate-500">{product.brand}</span>
                      )}
                      <span className="text-xs text-slate-400">•</span>
                      <span className="text-xs text-slate-500">₹{product.selling_price}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <span className={cn(
                      "text-xs font-medium px-2 py-0.5 rounded",
                      hasStock 
                        ? stock <= 5 
                          ? "bg-amber-100 text-amber-700" 
                          : "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-600"
                    )}>
                      {hasStock ? `${stock} in stock` : 'Out of stock'}
                    </span>
                    {isSelected && hasStock && (
                      <Check className="w-4 h-4 text-teal-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer Hint */}
          <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
            <div className="flex items-center justify-between text-[10px] text-slate-400">
              <span>↑↓ Navigate • Enter Select • Esc Close</span>
              <span>Auto-fills batch & price</span>
            </div>
          </div>
        </div>
      )}

      {/* No results */}
      {isOpen && searchTerm.length > 0 && filteredProducts.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg p-4">
          <div className="flex items-center gap-3 text-slate-500">
            <Package className="w-5 h-5" />
            <span className="text-sm">No products found for "{searchTerm}"</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickAddProduct;
